import { useState } from 'react'
import { api, errorText } from '../lib/api'
import { Button, useToast, cx } from './ui'

/**
 * Copies text to the clipboard.
 *
 * The async Clipboard API needs a secure context, which a plain-HTTP staging
 * host is not, so there is a fallback through a hidden textarea. Without it
 * the headline feature silently does nothing on exactly the setups people try
 * first.
 */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // Fall through to the legacy path rather than giving up.
  }

  try {
    const area = document.createElement('textarea')
    area.value = text
    area.setAttribute('readonly', '')
    area.style.position = 'fixed'
    area.style.opacity = '0'
    document.body.appendChild(area)
    area.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(area)
    return ok
  } catch {
    return false
  }
}

/**
 * Export for AI.
 *
 * Copies the chosen tasks as development context: link, description,
 * attachment links and discussion. Assignee, status, priority, tags and the
 * activity trail are deliberately left out — they are how the board is run,
 * not what somebody needs in order to write the code, and each one costs
 * context window.
 */
export function ExportButton({
  taskIds, label, size = 'md', variant = 'default', className,
}: {
  taskIds: string[]
  label?: string
  size?: 'sm' | 'md'
  variant?: 'default' | 'primary'
  className?: string
}) {
  const [busy, setBusy] = useState(false)
  const toast = useToast()

  async function run() {
    if (taskIds.length === 0) return
    setBusy(true)
    try {
      const result =
        taskIds.length === 1
          ? await api.exportTask(taskIds[0]!)
          : await api.exportTasks(taskIds)

      const copied = await copyText(result.markdown)
      if (!copied) {
        toast.show('Could not reach the clipboard', {
          tone: 'error',
          detail: 'Your browser blocked it',
        })
        return
      }

      toast.showCopied(
        result.tasks === 1 ? 'Task' : `${result.tasks} tasks`,
        result.tokens,
      )
    } catch (err) {
      toast.show('Export failed', {
        tone: 'error',
        detail: errorText(err),
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Button
      size={size}
      variant={variant}
      onClick={run}
      loading={busy}
      disabled={taskIds.length === 0}
      className={cx('gap-1.5', className)}
      title="Copy description, attachment links and comments as Markdown"
    >
      <SparkGlyph />
      {label ?? 'Export for AI'}
    </Button>
  )
}

function SparkGlyph() {
  return (
    <svg
      width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinejoin="round" aria-hidden
    >
      <path d="M6.2 2.2 7.3 5.1 10.2 6.2 7.3 7.3 6.2 10.2 5.1 7.3 2.2 6.2 5.1 5.1Z" />
      <path d="M11.5 9.2 12.1 10.8 13.7 11.4 12.1 12 11.5 13.6 10.9 12 9.3 11.4 10.9 10.8Z" />
    </svg>
  )
}
